/**
 * Diálogos de confirmación y aviso
 * -----------------------------------------------------------------------------
 *   const salir = await CompanyTheme.confirm({
 *     type: 'warning',
 *     title: '¿Salir sin guardar?',
 *     message: 'Se perderán los cambios.',
 *     confirmText: 'Salir',
 *   });
 *   if (salir) …
 *
 *   CompanyTheme.dialog.success('Registro guardado');
 *   CompanyTheme.dialog.error('No se pudo guardar', { detail: err.message });
 *
 * Qué cambia respecto de `Modalclear` (agro), de donde viene la idea:
 *
 *   · El modal SE QUITA del DOM al cerrarse. Allí se inyectaba con
 *     `insertAdjacentHTML` y se quedaba: al abrirlo por segunda vez había dos
 *     elementos con el mismo `id` en la página.
 *   · Los textos se ponen con `textContent`. Allí se interpolaban dentro del
 *     HTML sin escapar, y suelen venir del servidor.
 *   · Devuelve una promesa en vez de ejecutar la acción por dentro. Aquel modal
 *     llamaba a `clearForm()` y `mostrarSoloGrupoForm()`: un diálogo genérico
 *     no puede saber qué hace la aplicación al confirmar.
 *   · Hay variantes (éxito, error, aviso, pregunta), no solo la de advertencia.
 *   · Los textos de los botones salen del idioma configurado, no fijos en el
 *     código.
 */

import { dialogES } from './locale-es.js';

const ICONS = {
  success: 'fa-circle-check',
  error: 'fa-circle-xmark',
  warning: 'fa-triangle-exclamation',
  info: 'fa-circle-info',
  question: 'fa-circle-question',
};

/**
 * El color del botón NO sale del tipo de mensaje, sino de lo que hace la acción.
 * Son dos cosas distintas: el icono dice qué clase de aviso es —advertencia,
 * error, éxito— y el botón dice qué va a pasar al pulsarlo.
 *
 * Un diálogo de advertencia con un botón rojo hace pensar que se va a destruir
 * algo, cuando quizá solo se está saliendo de un formulario. El rojo se reserva
 * para lo que de verdad no tiene vuelta atrás, y eso lo declara quien llama con
 * `danger: true`.
 */
function confirmVariantOf({ danger, confirmVariant }) {
  if (confirmVariant) return `btn-${confirmVariant}`;
  return danger ? 'btn-danger' : 'btn-primary';
}

let Modal = null;
let uid = 0;

/**
 * Guarda el componente Modal de MDB para no depender de un global.
 */
export function setDialogComponents(components = {}) {
  if (components.Modal) Modal = components.Modal;
}

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text; // nunca innerHTML: el texto puede venir del servidor
  return node;
}

function build(config) {
  const { type, title, message, detail, confirmText, cancelText, showCancel } = config;
  uid += 1;

  const root = element('div', 'modal fade owari-dialog');
  root.id = `owari-dialog-${uid}`;
  root.tabIndex = -1;
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');

  const dialog = element('div', 'modal-dialog modal-dialog-centered modal-sm');
  const content = element('div', `modal-content owari-dialog-${type}`);

  const header = element('div', 'modal-header');
  const icon = element('i', `fas ${ICONS[type] || ICONS.info} owari-dialog-icon`);
  icon.setAttribute('aria-hidden', 'true');
  header.appendChild(icon);
  header.appendChild(element('h2', 'owari-dialog-title', title));
  content.appendChild(header);

  if (message || detail) {
    const body = element('div', 'modal-body');
    if (message) body.appendChild(element('p', 'owari-dialog-message', message));
    if (detail) body.appendChild(element('p', 'owari-dialog-detail', detail));
    content.appendChild(body);
  }

  const footer = element('div', 'modal-footer');

  if (showCancel) {
    const cancel = element('button', 'btn btn-outline-secondary', cancelText || dialogES.cancel);
    cancel.type = 'button';
    cancel.dataset.action = 'cancel';
    footer.appendChild(cancel);
  }

  const confirm = element(
    'button',
    `btn ${confirmVariantOf(config)}`,
    confirmText || (showCancel ? dialogES.confirm : dialogES.accept)
  );
  confirm.type = 'button';
  confirm.dataset.action = 'confirm';
  footer.appendChild(confirm);

  content.appendChild(footer);
  dialog.appendChild(content);
  root.appendChild(dialog);

  return root;
}

/**
 * Abre un diálogo y resuelve cuando se cierra.
 *
 * @param {Object} options
 * @param {string} [options.type] Tipo de MENSAJE: elige el icono y su color
 *   (`success`, `error`, `warning`, `info`, `question`).
 * @param {boolean} [options.danger] La ACCIÓN es destructiva: botón rojo y foco
 *   en cancelar. Es independiente del `type`.
 * @param {string} [options.confirmVariant] Color del botón a medida (`success`,
 *   `warning`…) cuando `danger` no alcanza.
 * @returns {Promise<boolean>} `true` si se confirmó, `false` si se canceló o se
 *   cerró con Escape, el fondo o la cruz.
 */
/**
 * Aparta los `<dialog>` abiertos mientras dura este modal, y los devuelve.
 *
 * **Un `<dialog>` abierto con `showModal()` vive en la capa de encima del
 * navegador**, por encima de cualquier `z-index`. Este modal es un elemento
 * corriente, así que abierto desde dentro de uno queda DEBAJO: se ve el fondo
 * oscurecerse, no se lee ni se pulsa nada, y parece que la aplicación se colgó.
 * Pasó al confirmar «quitar» desde el diálogo de papeles.
 *
 * No hay `z-index` que gane a la capa de encima, así que se cierran mientras se
 * pregunta y **se devuelven como estaban** al terminar: cerrarlos y no
 * devolverlos castiga por arrepentirse - hay que volver a abrir, volver a
 * desplegar y volver a buscar la línea -.
 *
 * @returns {() => void} la función que los devuelve a su sitio
 */
function apartarLosDeArriba() {
  const abiertos = Array.from(document.querySelectorAll('dialog[open]')).map((dialogo) => {
    let modal = true;

    try {
      modal = dialogo.matches(':modal');
    } catch {
      // `:modal` es reciente. Se asume que sí, que es como se abren todos los de
      // la suite: devolverlo con `show()` lo dejaría sin fondo y sin atrapar el
      // teclado.
    }

    return { dialogo, modal };
  });

  abiertos.forEach(({ dialogo }) => dialogo.close());

  return () => {
    // Al revés, para que el que estaba encima vuelva a quedar encima.
    abiertos.reverse().forEach(({ dialogo, modal }) => {
      if (dialogo.open || !dialogo.isConnected) return;

      if (modal) dialogo.showModal();
      else dialogo.show();
    });
  };
}

export function dialog(options = {}) {
  const config = {
    type: 'info',
    showCancel: false,
    dismissible: true,
    ...options,
  };

  const root = build(config);
  document.body.appendChild(root);

  // Antes de enseñar nada: si hay un `<dialog>` abierto, este modal saldría
  // debajo de él y no se vería.
  const devolverLosDeArriba = apartarLosDeArriba();

  return new Promise((resolve) => {
    let result = false;
    let settled = false;

    const instance = Modal ? new Modal(root, { backdrop: config.dismissible ? true : 'static', keyboard: config.dismissible }) : null;

    const finish = () => {
      // Idempotente: puede llegar por el evento de MDB o por la red de
      // seguridad, y no se sabe cuál gana.
      if (settled) return;
      settled = true;

      // Fuera del DOM al cerrarse: si no, cada apertura dejaría un modal —y su
      // `id`— dando vueltas por el documento.
      root.remove();
      // El backdrop es hermano del modal y MDB lo retira en su animación de
      // cierre; si esa animación no llega a terminar, hay que quitarlo a mano o
      // la página se queda con un velo gris encima.
      document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
      document.body.classList.remove('modal-open');

      // La pantalla queda como estaba: lo que se apartó para poder preguntar,
      // vuelve. Antes de resolver, para que quien esperaba la respuesta la
      // reciba con todo ya en su sitio.
      devolverLosDeArriba();

      resolve(result);
    };

    root.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-action]');
      if (!button) return;

      result = button.dataset.action === 'confirm';

      if (instance) {
        instance.hide();
        // Si la pestaña está en segundo plano el evento `hidden` no llega nunca:
        // sin esto el modal se quedaría en el DOM y la promesa sin resolver.
        setTimeout(finish, 500);
      } else {
        finish();
      }
    });

    root.addEventListener('hidden.mdb.modal', finish, { once: true });

    if (instance) {
      // En una acción destructiva el foco va a «cancelar», para que un Enter de
      // más no borre nada. En el resto va a «confirmar», que es lo que el
      // usuario suele querer.
      root.addEventListener(
        'shown.mdb.modal',
        () => {
          const selector = config.danger ? '[data-action="cancel"]' : '[data-action="confirm"]';
          const target = root.querySelector(selector) || root.querySelector('[data-action="confirm"]');
          if (target) target.focus();
        },
        { once: true }
      );

      instance.show();
    }
  });
}

/**
 * Diálogo de confirmación: dos botones y promesa con la respuesta.
 *
 * @returns {Promise<boolean>}
 */
export function confirm(options = {}) {
  return dialog({ type: 'question', showCancel: true, ...options });
}

['success', 'error', 'warning', 'info'].forEach((type) => {
  dialog[type] = (title, options = {}) => dialog({ ...options, type, title });
});

export default dialog;
